import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations';

const TheFieldPage = () => {
  const { language } = useLanguage();

  return (
    <main className="min-h-screen bg-[#FCF7EC] pt-20">
      {/* iQUBE and QUEST 9 side by side */}
      <section className="py-16 md:py-20 pb-6 md:pb-8 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <p className="text-xs tracking-[0.2em] uppercase font-sans text-teal mb-2">
              {t('field.label', language)}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-16">
            {/* THE iQUBE - Left */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h2 className="text-2xl md:text-3xl font-serif text-spruce mb-6 uppercase tracking-wide">
                {t('field.iqubeTitle', language)}
              </h2>
              <div className="space-y-4 text-deep-teal font-sans leading-relaxed text-sm md:text-base">
                <p>{t('field.iqubeP1', language)}</p>
                <p>{t('field.iqubeP2', language)}</p>
                <p>{t('field.iqubeP3', language)}</p>
              </div>
              <a
                href="https://quantumsoundtherapy.com/product/serenasphere9/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 text-teal underline text-sm font-sans hover:text-spruce transition-colors"
              >
                {language === 'fr' ? 'La Machine' : language === 'pt' ? 'A Máquina' : 'The Machine'}
              </a>
            </motion.div>

            {/* THE QUEST 9 - Right */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-2xl md:text-3xl font-serif text-spruce mb-6 uppercase tracking-wide">
                {t('field.questTitle', language)}
              </h2>
              <div className="space-y-4 text-deep-teal font-sans leading-relaxed text-sm md:text-base">
                <p>{t('field.questP1', language)}</p>
                <p>{t('field.questP2', language)}</p>
                <p>{t('field.questP3', language)}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE FREQUENCY CONTAINER - rectangular block */}
      <section className="py-6 md:py-8 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-serif text-spruce mb-8 uppercase tracking-wide text-center">
              {t('field.heroTitle', language)}
            </h2>
            <div
              className="mx-auto px-10 md:px-16 py-12 md:py-14 text-center"
              style={{ backgroundColor: '#F1E6D3' }}
            >
              <div className="max-w-3xl mx-auto space-y-4 text-deep-teal font-sans leading-relaxed text-sm md:text-base text-justify">
                <p>{t('field.introP1', language)}</p>
                <p>{t('field.introP2', language)}</p>
                <p>{t('field.introP3', language)}</p>
                <p>{t('field.introP4', language)}</p>
                <p>{t('field.introP5', language)}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Complementary approaches */}
      <section className="py-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="space-y-3 text-deep-teal font-sans leading-relaxed text-sm md:text-base">
              <p className="font-serif text-spruce italic">{t('field.compIntro', language)}</p>
              <p>{t('field.compIqube', language)}</p>
              <p>{t('field.compQuest', language)}</p>
              <p>{t('field.compTogether', language)}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* LIVING WITH THE FIELD */}
      <section className="py-16 px-6" style={{ backgroundColor: '#006868' }}>
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-serif text-white mb-8 uppercase tracking-wide">
              {t('field.livingTitle', language)}
            </h2>
            <div className="max-w-3xl space-y-4 text-white/90 font-sans leading-relaxed text-sm md:text-base">
              <p>{t('field.livingP1', language)}</p>
              <p>{t('field.livingP2', language)}</p>
              <p>{t('field.livingP3', language)}</p>
              <p>{t('field.livingP4', language)}</p>
              <p>{t('field.livingP5', language)}</p>
            </div>
            <div className="mt-10">
              <p className="text-xl md:text-2xl font-serif italic" style={{ color: '#FFBF00' }}>
                {t('field.livingCta', language)}
              </p>
              <p className="text-lg font-serif text-white/80 mt-2">
                {t('field.livingCta2', language)}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-spruce mb-6">
            {t('field.readyTitle', language)}
          </h2>
          <p className="text-deep-teal font-sans mb-8 text-sm md:text-base">
            {t('field.readyDesc', language)}
          </p>
          <Link
            to="/contact"
            className="inline-block btn-primary px-10 py-4 font-sans text-base tracking-wide rounded-full"
          >
            {t('nav.bookCall', language)}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default TheFieldPage;
